import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { select, Store } from '@ngrx/store';
import { FaxStatuses, FaxTypes } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { getProviderDisplayName } from 'src/app/common-components/helpers/provider-helper';
import { FaxRegisterListItem } from 'src/app/pages/fax-register/models/fax-register-results.model';
import { saveExpandCallDetailAdvancedSection } from 'src/app/store/user-preferences/user-preferences.actions';
import { getExpandCallDetailAdvancedSectionUserPreference } from 'src/app/store/user-preferences/user-preferences.selectors';
import { AppConfigService } from '../../app-config/app-config-service';
import { FaxRegisterApiService } from '../../pages/fax-register/services/fax-register-api.service';

// A received fax is saved once the transmission has finished, and a sent fax can sit in the queue and retry before it
// goes out, so createDate alone doesn't bound the call. Pad the log search window generously on the leading side.
const LOG_SEARCH_LEAD_MS = 15 * 60 * 1000;
const LOG_SEARCH_TRAIL_MS = 60 * 1000;

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'ndt-outgoing-fax-detail-dialog',
   styleUrls: ['./outgoing-fax-detail-dialog.component.scss'],
   templateUrl: './outgoing-fax-detail-dialog.component.html',
   standalone: false,
})
export class OutgoingFaxDetailDialogComponent {
   faxLogs: FaxRegisterListItem;
   columns: string[];
   faxStatuses = FaxStatuses;
   faxTypes = FaxTypes;
   isSystemUser: boolean;
   advancedSectionExpanded$: Observable<boolean>;

   constructor(
      private dialogRef: MatDialogRef<OutgoingFaxDetailDialogComponent>,
      private faxRegisterApiService: FaxRegisterApiService,
      @Inject(MAT_DIALOG_DATA) private data: any,
      private datePipe: DatePipe,
      private appConfig: AppConfigService,
      private store: Store,
      private clipboard: Clipboard,
      private snackBar: MatSnackBar
   ) {
      this.faxLogs = data.faxLogs;
      this.isSystemUser = data.isSystemUser ?? false;
      this.columns = ['DisplayName', 'Type', 'Status', 'Download'];
      this.advancedSectionExpanded$ = this.store.pipe(
         select(getExpandCallDetailAdvancedSectionUserPreference),
         map((expanded) => expanded ?? false)
      );
   }

   get providerName(): string {
      return getProviderDisplayName(this.faxLogs.associatedProviderId);
   }

   get serverDetails(): string {
      const { podName, podIp } = this.faxLogs;
      if (podName && podIp) {
         return `${podName} (${podIp})`;
      }
      return podName || podIp || '';
   }

   get logSearchFrom(): number {
      return new Date(this.faxLogs.createDate).getTime() - LOG_SEARCH_LEAD_MS;
   }

   get logSearchTo(): number {
      const times = [this.faxLogs.createDate, this.faxLogs.processedTime, this.faxLogs.updateDate]
         .filter((time) => {
            return time != null;
         })
         .map((time) => {
            return new Date(time as Date).getTime();
         });
      return Math.max(...times) + LOG_SEARCH_TRAIL_MS;
   }

   get homerSipCallIdLogsLink(): string {
      const homerUrl = this.appConfig.getConfig()?.homerUrl;
      return (
         `https://${homerUrl}/search/result?%7B%22timestamp%22%3A%7B%22from%22%3A` +
         this.logSearchFrom +
         '%2C%22to%22%3A' +
         this.logSearchTo +
         '%7D%2C%22param%22%3A%7B%22search%22%3A%7B%221_call%22%3A%7B%22callid%22%3A%5B%22' +
         this.faxLogs.sipCallId +
         '%22%5D%7D%7D%7D%7D='
      );
   }

   get graylogSipCallIdLogsLink(): string {
      return (
         'https://ndt-graylog.lcs.com/search?q=CallId%3A' +
         this.faxLogs.sipCallId +
         '&rangetype=absolute&from=' +
         this.datePipe.transform(new Date(this.logSearchFrom), 'yyyy-MM-ddTHH:mm:ss.SSSZ') +
         '&to=' +
         this.datePipe.transform(new Date(this.logSearchTo), 'yyyy-MM-ddTHH:mm:ss.SSSZ')
      );
   }

   downloadFile(fileToken: string, fileName: string): void {
      this.faxRegisterApiService.downloadFile(fileToken, fileName);
   }

   openLink(url: string) {
      window.open(url, '_blank');
   }

   copySipIdToClipboard(sipId: string) {
      const copied: boolean = this.clipboard.copy(sipId);

      if (copied) {
         this.snackBar.open('Copied to Clipboard');
      }
   }

   onAdvancedSectionToggled(expanded: boolean): void {
      this.store.dispatch(saveExpandCallDetailAdvancedSection({ expandCallDetailAdvancedSection: expanded }));
   }

   onCloseClick(): void {
      this.dialogRef.close();
   }
}
